import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { CheckCircleOutline as CheckCircleIcon } from '@mui/icons-material';
import SafeMarkdown from '../../shared/SafeMarkdown';
import { assistantChatMarkdownSx } from '../../shared/markdownBodySx';
import { colors } from '../../../designTokens';
import { assistantPrimaryButtonSx, assistantSecondaryButtonSx, assistantTextButtonSx } from '../../freestyle/assistantButtonStyles';

export function CreateFlowMetadataInviteCard({ onGenerate, onSkip, generating = false }) {
  return (
    <Stack spacing={1} sx={{ mt: 1.25 }}>
      <Typography variant="body2" sx={{ lineHeight: 1.5, color: colors.textPrimary }}>
        Would you like me to generate a <strong>title</strong>, a short <strong>summary</strong> (what it does and how to use it),
        <strong> category</strong>, <strong>tags</strong>, and <strong>where to use it</strong> for this capability?
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="contained" disabled={generating} onClick={onGenerate} sx={assistantPrimaryButtonSx}>
          {generating ? 'Generating…' : 'Generate description'}
        </Button>
        <Button size="small" variant="text" disabled={generating} onClick={onSkip} sx={assistantTextButtonSx}>
          Skip
        </Button>
      </Stack>
    </Stack>
  );
}

export function CreateFlowVariablesInviteCard({ onSuggest, onSkip, analyzing = false }) {
  return (
    <Stack spacing={1} sx={{ mt: 1.25 }}>
      <Typography variant="body2" sx={{ lineHeight: 1.5, color: colors.textPrimary }}>
        <strong>Variables (optional)</strong> — I can find text that should change each run and propose a version
        with placeholders like {'{{variable_name}}'} plus definitions for the builder.
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="contained" disabled={analyzing} onClick={onSuggest} sx={assistantPrimaryButtonSx}>
          {analyzing ? 'Analyzing…' : 'Suggest variables'}
        </Button>
        <Button size="small" variant="text" disabled={analyzing} onClick={onSkip} sx={assistantTextButtonSx}>
          Skip
        </Button>
      </Stack>
    </Stack>
  );
}

export function CreateFlowVariablesResultCard({
  summary = '',
  variableCount = 0,
  onApply,
  onSkip,
}) {
  return (
    <Stack spacing={1} sx={{ mt: 1.25 }}>
      <Typography variant="subtitle2" sx={{ color: colors.textPrimary }}>Variable suggestions</Typography>
      {summary ? <SafeMarkdown markdownBaseSx={assistantChatMarkdownSx}>{summary}</SafeMarkdown> : (
        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
          {variableCount > 0
            ? `Found ${variableCount} variable${variableCount === 1 ? '' : 's'} to templatize.`
            : 'No variables were suggested.'}
        </Typography>
      )}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="contained" onClick={onApply} sx={assistantPrimaryButtonSx}>
          Apply variables
        </Button>
        <Button size="small" variant="text" onClick={onSkip} sx={assistantTextButtonSx}>
          Keep current text
        </Button>
      </Stack>
    </Stack>
  );
}

export function CreateFlowFinalizeInviteCard({
  onGenerate,
  onSkip,
  onPreview,
  generating = false,
}) {
  return (
    <Stack spacing={1} sx={{ mt: 1.25 }}>
      <Typography variant="body2" sx={{ lineHeight: 1.5, color: colors.textPrimary }}>
        I can generate the details that make this capability easier to find and reuse: a <strong>title</strong>,
        <strong> description</strong>, <strong>category</strong>, <strong>tags</strong>, <strong>where to use it</strong>,
        and other searchable workspace details.
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="contained" disabled={generating} onClick={onGenerate} sx={assistantPrimaryButtonSx}>
          {generating ? 'Generating details…' : 'Generate details'}
        </Button>
        {onPreview && (
          <Button size="small" variant="text" disabled={generating} onClick={onPreview} sx={assistantTextButtonSx}>
            Preview in builder
          </Button>
        )}
        <Button size="small" variant="text" disabled={generating} onClick={onSkip} sx={assistantTextButtonSx}>
          Skip for now
        </Button>
      </Stack>
    </Stack>
  );
}

export function CreateFlowFinalizeCompleteCard({
  status = 'running',
  metadataApplied = false,
  variablesApplied = false,
  summary = '',
  error = '',
  onRun,
  onView,
  onSave,
}) {
  if (status === 'running') {
    return (
      <Typography variant="body2" sx={{ mt: 1.5, color: colors.textSecondary, lineHeight: 1.5 }}>
        Generating details…
      </Typography>
    );
  }
  if (status === 'error') {
    return (
      <Typography variant="body2" color="warning.main" sx={{ mt: 1.5, lineHeight: 1.45 }}>
        {error || 'Finalize step failed.'}
      </Typography>
    );
  }
  return (
    <Stack spacing={1.25} sx={{ mt: 1.25, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" spacing={0.85} alignItems="center">
        <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
        <Box>
          <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 700, fontSize: '0.9375rem' }}>
            Draft ready
          </Typography>
          <Typography variant="caption" sx={{ color: colors.textSecondary }}>
            {metadataApplied || variablesApplied ? 'Ready to test, review, and save.' : 'Ready for review.'}
          </Typography>
        </Box>
      </Stack>
      {summary && variablesApplied && (
        <SafeMarkdown markdownBaseSx={assistantChatMarkdownSx}>{summary}</SafeMarkdown>
      )}
      {(onRun || onView || onSave) && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {onRun && (
            <Button size="small" variant={onSave ? 'outlined' : 'contained'} onClick={onRun} sx={onSave ? assistantSecondaryButtonSx : assistantPrimaryButtonSx}>
              Run capability
            </Button>
          )}
          {onView && (
            <Button size="small" variant="outlined" onClick={onView} sx={assistantTextButtonSx}>
              View capability
            </Button>
          )}
          {onSave && (
            <Button size="small" variant="contained" onClick={onSave} sx={assistantPrimaryButtonSx}>
              Review and save
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}

export function CreateFlowFinalizeResultCard({
  metadataApplied = false,
  metadataError = '',
  summary = '',
  variableCount = 0,
  variablesError = '',
  onApply,
  onSkip,
  onPreview,
}) {
  return (
    <Stack spacing={1} sx={{ mt: 1.25 }}>
      <Typography variant="subtitle2" sx={{ color: colors.textPrimary }}>Capability details</Typography>
      {metadataApplied && (
        <Typography variant="body2" sx={{ color: colors.textPrimary, lineHeight: 1.5 }}>
          Title, summary, category, tags, and where to use have been filled in on your shelf.
        </Typography>
      )}
      <Typography variant="body2" sx={{ color: colors.textPrimary, lineHeight: 1.5 }}>
        {variableCount > 0
          ? `${variableCount} reusable variable${variableCount === 1 ? '' : 's'} ${variableCount === 1 ? 'is' : 'are'} already prepared with this draft.`
          : 'This capability does not need reusable variables.'}
      </Typography>
      {metadataError && (
        <Typography variant="body2" color="warning.main" sx={{ lineHeight: 1.45 }}>
          Description generation: {metadataError}
        </Typography>
      )}
      {summary ? (
        <SafeMarkdown markdownBaseSx={assistantChatMarkdownSx}>{summary}</SafeMarkdown>
      ) : (
        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
          {variableCount > 0
            ? `The draft is ready with ${variableCount} reusable variable${variableCount === 1 ? '' : 's'}.`
            : 'No reusable variables were needed.'}
        </Typography>
      )}
      {variablesError && (
        <Typography variant="body2" color="warning.main" sx={{ lineHeight: 1.45 }}>
          Variable preparation: {variablesError}
        </Typography>
      )}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="contained" onClick={onApply} sx={assistantPrimaryButtonSx}>
          Apply details
        </Button>
        {onPreview && (
          <Button size="small" variant="text" onClick={onPreview} sx={assistantTextButtonSx}>
            Preview in builder
          </Button>
        )}
        <Button size="small" variant="text" onClick={onSkip} sx={assistantTextButtonSx}>
          Keep current text
        </Button>
      </Stack>
    </Stack>
  );
}
